import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { usePageTitleStore } from '@/stores/pageTitleStore'
import { findNavItemByPath, findHubTabByPath } from '@/components/layout/navigation'

// Detail-page (":id") titles — these routes are not nav entries, so they cannot
// be derived from the navigation tree. Order matters: first match wins.
const detailTitles: { prefix: string; title: string }[] = [
  { prefix: '/tenant/settings/sso/access-review', title: 'Access Review' },
  { prefix: '/tenant/settings/sso/drift', title: 'SSO Drift Report' },
  // T2.6: engine consoles are routed pages but not nav/hub items.
  { prefix: '/tenant/airflow', title: 'Airflow Console' },
  { prefix: '/tenant/data/airflow', title: 'Airflow Console' },
  { prefix: '/tenant/dagster', title: 'Dagster Console' },
  { prefix: '/tenant/data/dagster', title: 'Dagster Console' },
  { prefix: '/tenant/workflows/', title: 'Workflow Detail' },
  // Build-hub create/detail routes (mirror navigation.ts dynamicRoutes). The
  // retired-tab routes have no hub-tab match, so without these the tab title
  // fell back to the hub name ("Build").
  { prefix: '/tenant/build/workflows/new', title: 'New Workflow' },
  { prefix: '/tenant/build/workflows/', title: 'Workflow Detail' },
  { prefix: '/tenant/build/agent-graphs/new', title: 'New Agent Graph' },
  { prefix: '/tenant/build/agent-graphs/generate', title: 'Generate Agent Graph' },
  { prefix: '/tenant/build/agent-graphs/', title: 'Agent Graph Detail' },
  { prefix: '/tenant/build/chains/generate', title: 'Generate Chain' },
  { prefix: '/tenant/build/chains/', title: 'Chain Detail' },
  { prefix: '/tenant/build/runbooks/new', title: 'New Runbook' },
  { prefix: '/tenant/build/runbooks/', title: 'Runbook Detail' },
  { prefix: '/tenant/executions/', title: 'Execution Detail' },
  { prefix: '/tenant/runs/', title: 'Run Detail' },
  // P2.147: same gap as Header.tsx's dynamicRoutes — these render directly
  // (not redirects) but were missing here, so the tab title fell back to the
  // generic 'Dashboard' for a loop/schedule/support-ticket detail page.
  { prefix: '/tenant/loops/', title: 'Loop Detail' },
  { prefix: '/tenant/schedules/', title: 'Schedule Detail' },
  { prefix: '/tenant/support/tickets/', title: 'Support Ticket Detail' },
  { prefix: '/tenant/templates/', title: 'Template Detail' },
  { prefix: '/tenant/clusters/', title: 'Cluster Detail' },
  { prefix: '/tenant/agent-graphs/', title: 'Agent Graph Detail' },
  { prefix: '/tenant/stategraphs/', title: 'Agent Graph Detail' },
  { prefix: '/tenant/runbooks/', title: 'Runbook Detail' },
  { prefix: '/tenant/chains/', title: 'Chain Detail' },
  // /tenant/ai/chat's own workaround entries were removed (P2.449): the
  // hub-tab-vs-nav-item match below is now longest-prefix-wins generically,
  // which already resolves the Chat tab correctly without a dedicated
  // prefix entry here. /tenant/chat/ (no "ai/") is a DIFFERENT, older route
  // with no nav-tree presence at all — findNavItemByPath/findHubTabByPath
  // can never resolve it, so it still needs its own entry.
  { prefix: '/tenant/chat/', title: 'AI Chat' },
  { prefix: '/tenant/catalog/', title: 'AI Catalog' },
  { prefix: '/platform/tenants/', title: 'Tenant Detail' },
  { prefix: '/platform/managed/clusters/', title: 'Cluster Detail' },
]

/**
 * Page title for the browser tab. Nav-reachable pages derive their title from
 * the navigation tree (single source of truth — same longest-prefix rule the
 * Header breadcrumbs use, so tab titles cannot drift from the sidebar). Detail
 * pages use the explicit prefix table above.
 */
export function getPageTitle(pathname: string): string {
  // Nav match FIRST: nav entries like /tenant/agents/skills are more specific
  // than the /tenant/agents/ detail prefix and must win.
  const navItem = findNavItemByPath(pathname)
  if (navItem && navItem.to === pathname) return navItem.name

  for (const d of detailTitles) {
    if (pathname === d.prefix || pathname.startsWith(d.prefix)) return d.title
  }
  // Agent detail: /tenant/agents/:id (after nav-exact + detail prefixes so
  // /tenant/agents/skills resolved above via the nav tree).
  if (pathname.startsWith('/tenant/agents/')) return 'Agent Detail'

  // P2.449: a hub sub-route (e.g. /tenant/build/workflows) also prefix-
  // matches its HUB's own flat nav item (e.g. /tenant/build, "Build") —
  // findNavItemByPath resolves that hub-root nav entry just as readily as
  // findHubTabByPath resolves the tab itself. Comparing by match length
  // (not "nav item always wins" / "hub tab always wins") picks whichever
  // matched `to` is more specific generically, the same longest-prefix
  // principle getBreadcrumbs already uses — a hub's own tabs are always
  // longer prefixes than the hub root, so this naturally prefers the tab
  // ("Definitions") over the hub ("Build") without hardcoding which
  // hub/tab pairs exist. Previously this checked flat nav items FIRST
  // unconditionally, so every hub sub-route's tab title fell back to the
  // hub's own name — see navigation.definitionsFacet.test.ts.
  const hubTab = findHubTabByPath(pathname)
  if (hubTab || navItem) {
    const hubLen = hubTab ? hubTab.tab.to.length : -1
    const navLen = navItem ? navItem.to.length : -1
    return hubLen >= navLen ? hubTab!.tab.name : navItem!.name
  }

  return 'Dashboard'
}

/**
 * Sets the browser tab title to: "TenantName · PageTitle · Vharta"
 * Helps distinguish tabs when user has multiple tenants open.
 */
export function useTenantPageTitle() {
  const user = useAuthStore((s) => s.user)
  const location = useLocation()
  const titleOverride = usePageTitleStore((s) => s.override)

  useEffect(() => {
    const pageTitle = titleOverride ?? getPageTitle(location.pathname)
    const tenantName = user?.tenantName
    document.title = tenantName
      ? `${tenantName} · ${pageTitle} · Vharta`
      : `${pageTitle} · Vharta Platform`

    return () => {
      document.title = 'Vharta Platform'
    }
  }, [location.pathname, user?.tenantName, titleOverride])
}
